import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BookmarkPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import type { MapFilters } from "@/components/MapFilterDrawer";
import {
  createSavedSearch,
  draftFromFilters,
  suggestName,
  type SearchContext,
} from "@/lib/saved-searches";

/**
 * "Save Search" dialog launched from the map filter panel.
 * Signed-out visitors are prompted to sign in instead of being redirected.
 */
export function SaveSearchDialog({
  open,
  onOpenChange,
  filters,
  context,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  filters: MapFilters;
  context?: SearchContext;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [alerts, setAlerts] = useState(true);

  const suggested = suggestName(filters, context);

  const saveM = useMutation({
    mutationFn: () => {
      const draft = draftFromFilters(filters, name.trim() || suggested, context);
      return createSavedSearch(user!.id, { ...draft, alerts_enabled: alerts });
    },
    onSuccess: () => {
      toast.success("Search saved.");
      qc.invalidateQueries({ queryKey: ["customer", "saved-searches"] });
      setName("");
      onOpenChange(false);
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Could not save this search."),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookmarkPlus className="h-4 w-4 text-brand" /> Save this search
          </DialogTitle>
          <DialogDescription>
            Store your purpose, property type, city, area, price range and distance so you can run
            this search again in a tap.
          </DialogDescription>
        </DialogHeader>

        {!user ? (
          <p className="rounded-xl bg-secondary p-3 text-sm text-brown-dark">
            Please sign in as a customer to save searches and receive alerts.
          </p>
        ) : (
          <div className="space-y-3">
            <div>
              <label
                htmlFor="saved-search-name"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                Search name
              </label>
              <input
                id="saved-search-name"
                value={name}
                placeholder={suggested}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand"
              />
            </div>

            <label className="flex items-start gap-2 rounded-xl border border-border p-3 text-sm">
              <input
                type="checkbox"
                checked={alerts}
                onChange={(e) => setAlerts(e.target.checked)}
                className="mt-0.5 accent-[color:var(--brand)]"
              />
              <span>
                <span className="font-semibold text-brown-dark">Enable search alerts</span>
                <span className="block text-xs text-muted-foreground">
                  Get an in-app notification whenever a newly approved listing matches.
                </span>
              </span>
            </label>
          </div>
        )}

        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            className="rounded-xl border border-border px-4 py-2 text-sm font-semibold text-brown-dark transition hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            disabled={!user || saveM.isPending}
            onClick={() => saveM.mutate()}
            className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-brown-dark shadow-soft transition hover:brightness-105 disabled:opacity-50"
          >
            {saveM.isPending ? "Saving…" : "Save Search"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
